// Route-config factory for the Circle / Network / Space admin index routes.
//
// Each kind's admin index route shares the same shape:
//   - `validateSearch` parses `?section=…&status=…&cursor=…`
//   - the component reads params + search, asserts session, wraps the
//     section body in `GroupAdminShell`
//
// The kind-specific bit is which sections exist and which component
// renders each section — that stays at the call site as a `renderSection`
// callback.

import type { ReactNode } from 'react';
import { getRouteApi, notFound, redirect } from '@tanstack/react-router';
import { isUuid } from '@leanin/shared';
import { getApiClient } from '@/lib/api-client';
import { useAuthedSession } from '@/lib/use-session';
import { GroupAdminShell } from '@/components/group/admin/GroupAdminShell';
import {
  resolveGroupAdminSection,
  type GroupAdminKind,
  type GroupAdminSection,
} from '@/components/group/admin/sections';
import {
  resolveBroadcastListStatus,
  type BroadcastListStatus,
} from '@/lib/queries/broadcasts';

export type GroupAdminSearch = {
  section?: GroupAdminSection;
  status?: BroadcastListStatus;
  cursor?: string;
  /** `?invite=1` opens the members section's invite modal on landing
   *  (onboarding "Invite members" deep link). */
  invite?: true;
};

export type AuthedSession = NonNullable<ReturnType<typeof useAuthedSession>>;

export type GroupAdminRenderArgs = {
  readonly id: string;
  readonly section: GroupAdminSection;
  readonly inviteOpen: boolean;
  readonly broadcastStatus: BroadcastListStatus;
  readonly broadcastCursor: string | undefined;
  readonly session: AuthedSession;
};

export type CreateGroupAdminRouteOpts = {
  readonly kind: GroupAdminKind;
  /** Route id matching the route file, e.g. `/_authed/circles/$id/admin/`. */
  readonly fromId: string;
  readonly renderSection: (args: GroupAdminRenderArgs) => ReactNode;
};

// Admin routes and their guards hit uuid-only endpoints, so a
// slug-addressed admin URL (someone editing /circles/<slug> into
// /circles/<slug>/admin by hand — in-app admin links always carry the uuid)
// must be resolved BEFORE the leader-or-admin gate fetches viewer-role with
// the raw param. Each kind's admin layout route calls this at the top of
// its beforeLoad: no-op for uuids, canonical-uuid redirect for slugs,
// notFound for unknown slugs (and for spaces, which have no slug-shaped
// detail mount).
const ADMIN_PATH_BY_KIND: Record<GroupAdminKind, '/circles/$id/admin' | '/networks/$id/admin' | '/groups/$id/admin'> = {
  circle: '/circles/$id/admin',
  network: '/networks/$id/admin',
  space: '/groups/$id/admin',
};

async function resolveGroupUuid(
  kind: GroupAdminKind,
  ref: string,
): Promise<string | null> {
  const client = getApiClient();
  if (kind === 'circle') {
    const { data } = await client.GET('/api/v1/circles/{id}', {
      params: { path: { id: ref } },
    });
    return data?.circle.id ?? null;
  }
  if (kind === 'network') {
    const { data } = await client.GET('/api/v1/networks/{id}', {
      params: { path: { id: ref } },
    });
    return data?.data.id ?? null;
  }
  return null;
}

export async function redirectSlugAdminUrlToUuid(
  kind: GroupAdminKind,
  id: string,
): Promise<void> {
  if (isUuid(id)) return;
  const uuid = await resolveGroupUuid(kind, id);
  if (uuid === null) throw notFound();
  throw redirect({
    to: ADMIN_PATH_BY_KIND[kind],
    params: { id: uuid },
    // Preserve ?section=…&status=…&cursor=… across the slug→uuid hop.
    search: true,
  });
}

export function createGroupAdminRoute(opts: CreateGroupAdminRouteOpts) {
  const { kind, fromId, renderSection } = opts;

  const validateSearch = (raw: Record<string, unknown>): GroupAdminSearch => {
    const status = resolveBroadcastListStatus(raw.status);
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    const invite =
      raw.invite === '1' || raw.invite === 1 || raw.invite === true || raw.invite === 'true';
    return {
      section: resolveGroupAdminSection(kind, raw.section),
      ...(status !== undefined ? { status } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
      ...(invite ? { invite: true } : {}),
    };
  };

  function GroupAdminRouteComponent() {
    // `getRouteApi(fromId)` re-resolves the typed Route bound to the file
    // route so `useParams()` / `useSearch()` carry the correct shape.
    const routeApi = getRouteApi(fromId as never);
    const { id } = routeApi.useParams() as { id: string };
    const search = routeApi.useSearch() as GroupAdminSearch;
    const section = search.section ?? 'overview';
    const broadcastStatus = search.status ?? 'all';
    const broadcastCursor = search.cursor;
    const session = useAuthedSession();

    if (session === null) return null;

    return (
      <GroupAdminShell id={id} kind={kind} section={section}>
        {renderSection({
          id,
          section,
          inviteOpen: search.invite === true,
          broadcastStatus,
          broadcastCursor,
          session,
        })}
      </GroupAdminShell>
    );
  }

  return {
    validateSearch,
    component: GroupAdminRouteComponent,
  };
}
